import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { briefingService } from 'services/briefingService';
import { briefingScheduleService } from 'services/briefingScheduleService';
import { parseApiError } from 'utils/errorParser';
import { notify } from 'features/ui/uiSlice';
import { MESSAGES } from 'constants/messages';

// ---------------------------------------------------------------------------
// Articles
// ---------------------------------------------------------------------------

export const fetchBriefing = createAsyncThunk('briefing/fetch', async (params, { rejectWithValue }) => {
  try {
    return await briefingService.list(params);
  } catch (error) {
    return rejectWithValue(parseApiError(error));
  }
});

export const fetchBatches = createAsyncThunk('briefing/batches', async (_, { rejectWithValue }) => {
  try {
    return await briefingService.listBatches();
  } catch (error) {
    return rejectWithValue(parseApiError(error));
  }
});

export const setBriefingDecision = createAsyncThunk(
  'briefing/decision',
  async ({ id, decision, documentVersion }, { dispatch, rejectWithValue }) => {
    try {
      const updated = await briefingService.setDecision(id, decision, documentVersion);
      dispatch(notify({ type: 'success', message: MESSAGES.PIPELINE.DECISION_SAVED }));
      return updated;
    } catch (error) {
      const parsed = parseApiError(error);
      dispatch(notify({ type: 'error', message: parsed.message }));
      return rejectWithValue(parsed);
    }
  }
);

export const dispatchBriefingArticle = createAsyncThunk(
  'briefing/dispatch',
  async ({ id, documentVersion }, { dispatch, rejectWithValue }) => {
    try {
      const updated = await briefingService.dispatch(id, documentVersion);
      dispatch(notify({ type: 'success', message: MESSAGES.PIPELINE.DISPATCHED }));
      dispatch(fetchBatches());
      return updated;
    } catch (error) {
      const parsed = parseApiError(error);
      dispatch(notify({ type: 'error', message: parsed.message }));
      return rejectWithValue(parsed);
    }
  }
);

export const dispatchWithDecision = createAsyncThunk(
  'briefing/dispatchWithDecision',
  async ({ id, decision, documentVersion }, { dispatch, rejectWithValue }) => {
    try {
      const decided = await briefingService.setDecision(id, decision, documentVersion);
      const dispatched = await briefingService.dispatch(id, decided.documentVersion);
      dispatch(notify({ type: 'success', message: MESSAGES.PIPELINE.DISPATCHED }));
      dispatch(fetchBatches());
      return dispatched;
    } catch (error) {
      const parsed = parseApiError(error);
      dispatch(notify({ type: 'error', message: parsed.message }));
      return rejectWithValue(parsed);
    }
  }
);

export const bulkDispatchArticles = createAsyncThunk(
  'briefing/bulkDispatch',
  async ({ items, decision }, { dispatch, rejectWithValue }) => {
    try {
      const results = await Promise.allSettled(
        items.map(async (item) => {
          let currentVersion = item.documentVersion;
          if (decision && decision !== item.briefingDecision) {
            const decided = await briefingService.setDecision(item.id, decision, currentVersion);
            currentVersion = decided.documentVersion;
          }
          return await briefingService.dispatch(item.id, currentVersion);
        })
      );
      
      const successes = results.filter(r => r.status === 'fulfilled');
      const failures = results.filter(r => r.status === 'rejected');
      
      if (successes.length > 0) {
        dispatch(notify({ type: 'success', message: `Successfully dispatched ${successes.length} articles.` }));
      }
      if (failures.length > 0) {
        dispatch(notify({ type: 'error', message: `Failed to dispatch ${failures.length} articles.` }));
      }
      
      dispatch(fetchBatches());
      return successes.map(r => r.value);
    } catch (error) {
      const parsed = parseApiError(error);
      dispatch(notify({ type: 'error', message: parsed.message }));
      return rejectWithValue(parsed);
    }
  }
);

// ---------------------------------------------------------------------------
// Schedule (singleton)
// ---------------------------------------------------------------------------

export const fetchSchedule = createAsyncThunk('briefing/fetchSchedule', async (_, { rejectWithValue }) => {
  try {
    return await briefingScheduleService.get();
  } catch (error) {
    return rejectWithValue(parseApiError(error));
  }
});

export const saveSchedule = createAsyncThunk('briefing/saveSchedule', async (model, { dispatch, rejectWithValue }) => {
  try {
    const saved = await briefingScheduleService.upsert(model);
    dispatch(notify({ type: 'success', message: 'Schedule saved.' }));
    return saved;
  } catch (error) {
    const parsed = parseApiError(error);
    dispatch(notify({ type: 'error', message: parsed.message }));
    return rejectWithValue(parsed);
  }
});

export const toggleSchedule = createAsyncThunk('briefing/toggleSchedule', async ({ schedule, nextStatus }, { dispatch, rejectWithValue }) => {
  try {
    const updated = await briefingScheduleService.upsert({ ...schedule, status: nextStatus });
    dispatch(notify({ type: 'success', message: `Schedule ${nextStatus === 'active' ? 'activated' : 'deactivated'}.` }));
    return updated;
  } catch (error) {
    const parsed = parseApiError(error);
    dispatch(notify({ type: 'error', message: parsed.message }));
    return rejectWithValue(parsed);
  }
});

// ---------------------------------------------------------------------------
// Slice
// ---------------------------------------------------------------------------

const briefingSlice = createSlice({
  name: 'briefing',
  initialState: {
    items: [],
    batches: [],
    status: 'idle',
    saving: false,
    error: null,
    schedule: null,
    scheduleStatus: 'idle',
    scheduleSaving: false,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchBriefing.pending, (state) => { state.status = 'loading'; })
      .addCase(fetchBriefing.fulfilled, (state, action) => { state.status = 'succeeded'; state.items = action.payload; })
      .addCase(fetchBriefing.rejected, (state, action) => { state.status = 'failed'; state.error = action.payload?.message; })
      .addCase(fetchBatches.fulfilled, (state, action) => { state.batches = action.payload; })
      .addCase(setBriefingDecision.pending, (state) => { state.saving = true; })
      .addCase(setBriefingDecision.fulfilled, (state, action) => {
        state.saving = false;
        const idx = state.items.findIndex((i) => i.id === action.payload.id);
        if (idx !== -1) state.items[idx] = action.payload;
      })
      .addCase(setBriefingDecision.rejected, (state) => { state.saving = false; })
      .addCase(dispatchBriefingArticle.pending, (state) => { state.saving = true; })
      .addCase(dispatchBriefingArticle.fulfilled, (state, action) => {
        state.saving = false;
        const idx = state.items.findIndex((i) => i.id === action.payload.id);
        if (idx !== -1) state.items[idx] = action.payload;
      })
      .addCase(dispatchBriefingArticle.rejected, (state) => { state.saving = false; })
      .addCase(dispatchWithDecision.pending, (state) => { state.saving = true; })
      .addCase(dispatchWithDecision.fulfilled, (state, action) => {
        state.saving = false;
        const idx = state.items.findIndex((i) => i.id === action.payload.id);
        if (idx !== -1) state.items[idx] = action.payload;
      })
      .addCase(dispatchWithDecision.rejected, (state) => { state.saving = false; })
      .addCase(bulkDispatchArticles.pending, (state) => { state.saving = true; })
      .addCase(bulkDispatchArticles.fulfilled, (state, action) => {
        state.saving = false;
        action.payload.forEach(payloadItem => {
          const idx = state.items.findIndex((i) => i.id === payloadItem.id);
          if (idx !== -1) state.items[idx] = payloadItem;
        });
      })
      .addCase(bulkDispatchArticles.rejected, (state) => { state.saving = false; })
      // Schedule
      .addCase(fetchSchedule.pending, (state) => { state.scheduleStatus = 'loading'; })
      .addCase(fetchSchedule.fulfilled, (state, action) => { state.scheduleStatus = 'succeeded'; state.schedule = action.payload; })
      .addCase(fetchSchedule.rejected, (state) => { state.scheduleStatus = 'failed'; })
      .addCase(saveSchedule.pending, (state) => { state.scheduleSaving = true; })
      .addCase(saveSchedule.fulfilled, (state, action) => { state.scheduleSaving = false; state.schedule = action.payload; })
      .addCase(saveSchedule.rejected, (state) => { state.scheduleSaving = false; })
      .addCase(toggleSchedule.pending, (state) => { state.scheduleSaving = true; })
      .addCase(toggleSchedule.fulfilled, (state, action) => { state.scheduleSaving = false; state.schedule = action.payload; })
      .addCase(toggleSchedule.rejected, (state) => { state.scheduleSaving = false; });
  },
});

export default briefingSlice.reducer;
